import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Restores scroll position of a custom scroll container across browser
 * back/forward navigation.
 *
 * Why this exists:
 * - The app scrolls a custom div (`topicMainContent`) instead of the window,
 *   so the browser's native scroll restoration and React Router's
 *   <ScrollRestoration> (data-router only) cannot help.
 * - Topic content is fetched asynchronously after navigation, so the
 *   container has height 0 at the moment of POP. We wait via a
 *   MutationObserver until it grows tall enough, then restore.
 *
 * Saved positions are keyed by `location.key` so each entry in the history
 * stack has its own remembered scrollTop. We also persist to sessionStorage
 * so a hard refresh during a session keeps positions.
 */

const STORAGE_KEY = "scrollRestoration:v1";
const MAX_WAIT_MS = 4000;
const SAVE_DEBOUNCE_MS = 100;
// Max number of position entries to keep in sessionStorage. Each location.key
// gets its own entry; without a cap the map grows unbounded for long sessions
// and eventually risks the storage quota. 100 is far more than any realistic
// browser history depth.
const MAX_POSITIONS = 100;
// After mutations stop AND scrollHeight has not changed for this long, we
// treat the new content as "settled" and reveal the container.
const SETTLE_MS = 600;
// Don't reveal earlier than this even if content appears settled. Topic pages
// load content in multiple bursts with ~1s gaps between them; revealing too
// early causes the user to see the next burst push the scroll position.
const MIN_HIDE_MS = 1200;

const loadAll = () => {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
};

const saveAll = (map) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* ignore quota / disabled storage */
  }
};

// Write `value` against `key` in `map`, evicting the oldest entries so the
// map never exceeds MAX_POSITIONS. Relies on the fact that string-keyed
// object property iteration order matches insertion order in modern JS
// engines — deleting then re-assigning moves the key to the end (most
// recently used).
const setPosition = (map, key, value) => {
  if (key in map) delete map[key];
  map[key] = value;
  const keys = Object.keys(map);
  if (keys.length > MAX_POSITIONS) {
    const overflow = keys.length - MAX_POSITIONS;
    for (let i = 0; i < overflow; i++) delete map[keys[i]];
  }
};

export function useScrollRestoration(scrollContainerRef, options) {
  const enabled = options ? options.enabled !== false : true;
  const location = useLocation();
  const navigationType = useNavigationType();
  const positionsRef = useRef(loadAll());
  const currentKeyRef = useRef(location.key);
  // Tracks the most recent non-zero scrollTop we observed for the current
  // location. Used as a fallback when something (legacy jQuery scroll_view,
  // resetState, sidebar close, etc.) resets el.scrollTop to 0 just before a
  // navigation fires — otherwise that 0 would be saved against the
  // outgoing key and corrupt the back-button restore.
  const lastSeenScrollRef = useRef(0);
  // While true, scroll events are ignored for saving (they are caused by
  // our restore / by the browser clamping scrollTop while content rebuilds).
  const restoringRef = useRef(false);

  // Disable browser's own scroll restoration so it stops fighting us.
  useEffect(() => {
    if (!enabled) return;
    if ("scrollRestoration" in window.history) {
      const prev = window.history.scrollRestoration;
      window.history.scrollRestoration = "manual";
      return () => {
        window.history.scrollRestoration = prev;
      };
    }
  }, []);

  // Persist current container scrollTop while the user scrolls.
  useEffect(() => {
    if (!enabled) return;
    const el = scrollContainerRef?.current;
    if (!el) return;

    let timer = null;
    const onScroll = () => {
      if (restoringRef.current) return;
      // Capture every non-zero top immediately so a subsequent reset-to-0
      // cannot erase the memory of where the user actually was.
      const immediate = el.scrollTop;
      if (immediate > 0) lastSeenScrollRef.current = immediate;
      if (timer) return;
      timer = setTimeout(() => {
        timer = null;
        const top = el.scrollTop;
        // If we get a 0 here but had previously seen a real scroll position,
        // assume legacy code reset us and skip the write — don't clobber the
        // good saved value. (User intentionally scrolling to top is rare and
        // will be re-saved on the next location change anyway.)
        if (top === 0 && lastSeenScrollRef.current > 0) return;
        if (top > 0) lastSeenScrollRef.current = top;
        setPosition(positionsRef.current, currentKeyRef.current, top);
        saveAll(positionsRef.current);
      }, SAVE_DEBOUNCE_MS);
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
      if (timer) clearTimeout(timer);
    };
  }, [scrollContainerRef]);

  // On location change: save the outgoing position synchronously, then
  // either restore (POP) or scroll to top (PUSH/REPLACE).
  useEffect(() => {
    if (!enabled) return;
    const el = scrollContainerRef?.current;

    if (!el) {
      currentKeyRef.current = location.key;
      return;
    }

    // Save the position we are leaving against the previous key. If the
    // live scrollTop is 0 but we observed a real scroll earlier, fall back
    // to that — see lastSeenScrollRef comment above.
    const liveTop = el.scrollTop;
    const leavingTop = liveTop > 0 ? liveTop : lastSeenScrollRef.current;
    if (leavingTop > 0 || !(currentKeyRef.current in positionsRef.current)) {
      setPosition(positionsRef.current, currentKeyRef.current, leavingTop);
      saveAll(positionsRef.current);
    }

    currentKeyRef.current = location.key;
    // Reset for the new page — fresh tracking starts at 0.
    lastSeenScrollRef.current = 0;

    if (navigationType === "POP") {
      const target = positionsRef.current[location.key] ?? 0;

      // Enter "restoring" mode: ignore scroll events and keep re-applying
      // the target until the user actually interacts or we time out. This
      // protects against intermediate DOM rebuilds (e.g. resetState() on
      // POP) that would otherwise clamp scrollTop to 0 and clobber the
      // saved value.
      restoringRef.current = true;

      // Hide the container during restoration so the user does not see the
      // stale (previous page's) content scrolling to the saved position
      // before the new content loads. We use opacity (not display/visibility)
      // so layout / scrollHeight measurements still work AND so the scrollbar
      // position changes are not visible either.
      const prevOpacity = el.style.opacity;
      const prevPointerEvents = el.style.pointerEvents;
      const prevTransition = el.style.transition;
      el.style.transition = "none";
      el.style.opacity = "0";
      el.style.pointerEvents = "none";

      let cancelled = false;
      let timeoutId = null;
      let settleTimerId = null;
      let revealed = false;

      const reveal = (reason) => {
        if (revealed) return;
        revealed = true;
        el.style.opacity = prevOpacity || "";
        el.style.pointerEvents = prevPointerEvents || "";
        el.style.transition = prevTransition || "";
      };

      const tryRestore = (reason) => {
        if (cancelled) return false;
        const maxScroll = el.scrollHeight - el.clientHeight;
        const ready = maxScroll >= target;
        if (ready) {
          el.scrollTop = target;
        }
        return ready;
      };

      tryRestore("immediate");

      // Keep observing — content may rebuild (resetState then refetch) and
      // each rebuild needs us to re-apply the target. Whenever a mutation
      // happens we restart the "settle" timer; once mutations stop AND
      // scrollHeight has not changed for SETTLE_MS we consider the new
      // content stable and reveal it. Without the scrollHeight check we
      // sometimes revealed during a brief lull, then the user would see
      // subsequent content loads shift the scroll position.
      const startedAt = Date.now();
      let settleScrollHeight = -1;
      const scheduleSettle = () => {
        if (settleTimerId) clearTimeout(settleTimerId);
        settleScrollHeight = el.scrollHeight;
        const elapsed = Date.now() - startedAt;
        const wait = Math.max(SETTLE_MS, MIN_HIDE_MS - elapsed);
        settleTimerId = setTimeout(() => {
          const currentH = el.scrollHeight;
          if (currentH !== settleScrollHeight) {
            scheduleSettle();
            return;
          }
          if (Date.now() - startedAt < MIN_HIDE_MS) {
            scheduleSettle();
            return;
          }
          tryRestore("settle");
          reveal("settled");
        }, wait);
      };

      const observer = new MutationObserver(() => {
        tryRestore("mutation");
        scheduleSettle();
      });
      observer.observe(el, {
        childList: true,
        subtree: true,
        characterData: true,
      });
      scheduleSettle();

      // Stop restoring as soon as the user genuinely interacts.
      const stopOnUserInput = (ev) => {
        cleanup();
      };
      const userEvents = ["wheel", "touchstart", "keydown", "mousedown"];
      userEvents.forEach((evt) => el.addEventListener(evt, stopOnUserInput, { passive: true }));

      const cleanup = () => {
        if (cancelled) return;
        cancelled = true;
        restoringRef.current = false;
        observer.disconnect();
        if (timeoutId) clearTimeout(timeoutId);
        if (settleTimerId) clearTimeout(settleTimerId);
        userEvents.forEach((evt) => el.removeEventListener(evt, stopOnUserInput));
        reveal("cleanup");
      };

      timeoutId = setTimeout(() => {
        tryRestore("timeout-final");
        cleanup();
      }, MAX_WAIT_MS);

      return cleanup;
    } else {
      // New navigation: start at top.
      restoringRef.current = true;
      el.scrollTop = 0;
      requestAnimationFrame(() => {
        restoringRef.current = false;
      });
    }
  }, [location.key]);
}

export default useScrollRestoration;
